import { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { listarMascotas, type MascotaLista, type Recordatorio } from "../api";
import { formatearFecha } from "../formato";
import AvatarMascota from "../componentes/AvatarMascota";

export default function MisMascotas() {
  const mascotas = useQuery({ queryKey: ["mascotas"], queryFn: listarMascotas });
  const [busqueda, setBusqueda] = useState("");
  const [filtroEspecie, setFiltroEspecie] = useState<string>("todas");

  const lista = mascotas.data ?? [];

  // Extraer especies únicas presentes en las mascotas del usuario
  const especiesDisponibles = useMemo(() => {
    const set = new Set<string>();
    for (const m of lista) {
      if (m.especie?.trim()) set.add(m.especie.trim());
    }
    return Array.from(set).sort();
  }, [lista]);

  // Contar mascotas con al menos una vacuna o control vencido
  const totalConAtrasos = useMemo(() => {
    return lista.filter((m) => m.recordatorios.some((r) => r.estado === "Vencido")).length;
  }, [lista]);

  // Filtrado reactivo en memoria
  const mascotasFiltradas = useMemo(() => {
    return lista.filter((m) => {
      // 1. Filtro por píldora
      if (filtroEspecie === "atrasadas") {
        const tieneAtrasados = m.recordatorios.some((r) => r.estado === "Vencido");
        if (!tieneAtrasados) return false;
      } else if (filtroEspecie !== "todas") {
        if (m.especie.trim().toLowerCase() !== filtroEspecie.toLowerCase()) return false;
      }

      // 2. Filtro por texto de búsqueda (nombre, especie, raza, color)
      if (busqueda.trim()) {
        const q = busqueda.toLowerCase().trim();
        const matchNombre = m.nombre.toLowerCase().includes(q);
        const matchEspecie = m.especie.toLowerCase().includes(q);
        const matchRaza = m.raza?.toLowerCase().includes(q) ?? false;
        const matchColor = m.color?.toLowerCase().includes(q) ?? false;
        return matchNombre || matchEspecie || matchRaza || matchColor;
      }

      return true;
    });
  }, [lista, filtroEspecie, busqueda]);

  const hayFiltroActivo = busqueda.trim() !== "" || filtroEspecie !== "todas";

  return (
    <>
      <div className="titulo-fila">
        <h1>Mis mascotas</h1>
        <Link className="boton-primario" to="/mascotas/nueva">
          + Agregar mascota
        </Link>
      </div>

      {mascotas.isPending && (
        <div className="tarjetas tarjetas-mascotas" aria-label="Cargando mascotas…">
          <div className="panel tarjeta esqueleto-tarjeta">
            <div className="esqueleto-cab">
              <div className="esqueleto-avatar" />
              <div className="esqueleto-linea titulo" />
            </div>
            <div className="esqueleto-cuerpo">
              <div className="esqueleto-linea" />
              <div className="esqueleto-linea corta" />
              <div className="esqueleto-linea" />
            </div>
          </div>
          <div className="panel tarjeta esqueleto-tarjeta">
            <div className="esqueleto-cab">
              <div className="esqueleto-avatar" />
              <div className="esqueleto-linea titulo" />
            </div>
            <div className="esqueleto-cuerpo">
              <div className="esqueleto-linea" />
              <div className="esqueleto-linea corta" />
              <div className="esqueleto-linea" />
            </div>
          </div>
          <div className="panel tarjeta esqueleto-tarjeta">
            <div className="esqueleto-cab">
              <div className="esqueleto-avatar" />
              <div className="esqueleto-linea titulo" />
            </div>
            <div className="esqueleto-cuerpo">
              <div className="esqueleto-linea" />
              <div className="esqueleto-linea corta" />
              <div className="esqueleto-linea" />
            </div>
          </div>
        </div>
      )}
      {mascotas.error && <p className="err">{mascotas.error.message}</p>}

      {!mascotas.isPending && !mascotas.error && lista.length === 0 && (
        <div className="panel vacio">
          <p>Todavía no tienes mascotas registradas.</p>
          <Link className="boton-primario" to="/mascotas/nueva">
            Agregar la primera
          </Link>
        </div>
      )}

      {/* Buscador y Filtros Rápidos (solo si hay mascotas) */}
      {lista.length > 0 && (
        <div className="buscador-mascotas-panel">
          <div className="buscador-mascotas-input-wrap">
            <span className="buscador-mascotas-icono" aria-hidden="true">🔍</span>
            <input
              type="search"
              className="buscador-mascotas-input"
              placeholder="Buscar por nombre, raza o especie…"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              aria-label="Buscar mascota"
            />
            {busqueda && (
              <button
                type="button"
                className="buscador-mascotas-limpiar"
                onClick={() => setBusqueda("")}
                title="Limpiar búsqueda"
                aria-label="Limpiar búsqueda"
              >
                ✕
              </button>
            )}
          </div>

          <div className="buscador-mascotas-filtros">
            <button
              type="button"
              className={`filtro-pildora ${filtroEspecie === "todas" ? "activa" : ""}`}
              onClick={() => setFiltroEspecie("todas")}
            >
              Todas ({lista.length})
            </button>
            {especiesDisponibles.map((esp) => {
              const cant = lista.filter((m) => m.especie.trim().toLowerCase() === esp.toLowerCase()).length;
              return (
                <button
                  key={esp}
                  type="button"
                  className={`filtro-pildora ${filtroEspecie.toLowerCase() === esp.toLowerCase() ? "activa" : ""}`}
                  onClick={() => setFiltroEspecie(esp)}
                >
                  {esp} ({cant})
                </button>
              );
            })}
            {totalConAtrasos > 0 && (
              <button
                type="button"
                className={`filtro-pildora filtro-pildora-alerta ${filtroEspecie === "atrasadas" ? "activa" : ""}`}
                onClick={() => setFiltroEspecie("atrasadas")}
                title="Mostrar solo mascotas con vacunas o controles vencidos"
              >
                ⚠️ Con atrasos ({totalConAtrasos})
              </button>
            )}
          </div>

          {hayFiltroActivo && (
            <div className="resultados-conteo muted">
              <span>
                Mostrando {mascotasFiltradas.length} de {lista.length}{" "}
                {lista.length === 1 ? "mascota" : "mascotas"}
              </span>
              <button
                type="button"
                className="boton-link-limpiar"
                onClick={() => {
                  setBusqueda("");
                  setFiltroEspecie("todas");
                }}
              >
                Restablecer filtros
              </button>
            </div>
          )}
        </div>
      )}

      {/* Mensaje de no coincidencias */}
      {lista.length > 0 && mascotasFiltradas.length === 0 && (
        <div className="panel vacio" style={{ marginTop: "1rem" }}>
          <p>No se encontraron mascotas que coincidan con la búsqueda o filtro aplicado.</p>
          <button
            type="button"
            className="boton"
            onClick={() => {
              setBusqueda("");
              setFiltroEspecie("todas");
            }}
          >
            Ver todas las mascotas
          </button>
        </div>
      )}

      {/* Grilla de mascotas filtradas */}
      {mascotasFiltradas.length > 0 && (
        <div className="tarjetas tarjetas-mascotas">
          {mascotasFiltradas.map((m) => (
            <TarjetaMascota key={m.mascotaID} mascota={m} />
          ))}
        </div>
      )}
    </>
  );
}

function TarjetaMascota({ mascota }: { mascota: MascotaLista }) {
  const navegar = useNavigate();
  const pendientes = mascota.recordatorios;
  const atrasados = pendientes.filter((r) => r.estado === "Vencido").length;

  return (
    <div
      className="panel tarjeta tarjeta-mascota"
      onClick={() => navegar(`/mascotas/${mascota.mascotaID}`)}
      role="button"
      tabIndex={0}
      title={`Ver ficha clínica de ${mascota.nombre}`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navegar(`/mascotas/${mascota.mascotaID}`);
        }
      }}
    >
      <header className="tarjeta-cab">
        <div className="tarjeta-con-avatar">
          <AvatarMascota
            nombre={mascota.nombre}
            especie={mascota.especie}
            fotoPerfilUrl={mascota.fotoPerfilUrl}
            tamano="md"
          />
          <h2 className="tarjeta-nombre">{mascota.nombre}</h2>
        </div>
        <div className="tarjeta-badges">
          {mascota.estado !== "Activa" && (
            <span className="etiqueta neutra">{mascota.estado}</span>
          )}
          {atrasados > 0 && (
            <span className="etiqueta vencido">
              {atrasados} {atrasados === 1 ? "atrasado" : "atrasados"}
            </span>
          )}
          <span className="tarjeta-flecha" aria-hidden="true" title="Ver ficha">
            →
          </span>
        </div>
      </header>

      <dl className="datos">
        <dt>Especie</dt>
        <dd>
          {mascota.especie}
          {mascota.raza ? ` · ${mascota.raza}` : ""}
        </dd>
        <dt>Sexo</dt>
        <dd>
          {mascota.sexo}
          {mascota.color ? ` · ${mascota.color}` : ""}
        </dd>
        {mascota.edad && (
          <>
            <dt>Edad</dt>
            <dd>{mascota.edad}</dd>
          </>
        )}
        {mascota.pesoKg != null && (
          <>
            <dt>Peso</dt>
            <dd>
              {mascota.pesoKg.toFixed(1)} kg
              <span className="muted"> · {formatearFecha(mascota.pesoFecha)}</span>
            </dd>
          </>
        )}
      </dl>

      <section className="seguimiento">
        <h3>Seguimiento</h3>
        {pendientes.length === 0 ? (
          <p className="muted">
            Sin controles pendientes. Se agendan solos al registrar una visita.
          </p>
        ) : (
          <ul className="recordatorios" onClick={(e) => e.stopPropagation()}>
            {pendientes.map((r) => (
              <FilaRecordatorio
                key={r.recordatorioID}
                recordatorio={r}
                mascotaID={mascota.mascotaID}
                puedeEditar={mascota.puedeEditar}
              />
            ))}
          </ul>
        )}
      </section>

      <footer className="tarjeta-acciones-pie" onClick={(e) => e.stopPropagation()}>
        <Link
          to={`/mascotas/${mascota.mascotaID}/visitas/nueva`}
          className="boton-primario boton-chico tarjeta-boton-visita"
          title={`Registrar atención o visita médica para ${mascota.nombre}`}
        >
          + Registrar visita
        </Link>
      </footer>
    </div>
  );
}

const ETIQUETA_ESTADO: Record<Recordatorio["estado"], string> = {
  Vencido: "vencido",
  PorVencer: "por-vencer",
  AlDia: "al-dia",
};

function FilaRecordatorio({
  recordatorio,
  mascotaID,
  puedeEditar,
}: {
  recordatorio: Recordatorio;
  mascotaID: string;
  puedeEditar: boolean;
}) {
  const claseEstado = ETIQUETA_ESTADO[recordatorio.estado];
  const urlAplicar = `/mascotas/${mascotaID}/visitas/nueva?productoNombre=${encodeURIComponent(
    recordatorio.titulo,
  )}&motivo=${encodeURIComponent(`Aplicación de ${recordatorio.titulo}`)}`;

  if (puedeEditar) {
    return (
      <li className={`recordatorio ${claseEstado} recordatorio-interactivo`}>
        <Link
          to={urlAplicar}
          className="rec-link-aplicar"
          title={`Hacer clic para registrar aplicación de ${recordatorio.titulo}`}
          onClick={(e) => e.stopPropagation()}
        >
          <span className="rec-titulo">
            <span className="rec-icono-rayo" aria-hidden="true">
              ⚡
            </span>
            {recordatorio.titulo}
          </span>
          <span className="rec-fecha">
            {formatearFecha(recordatorio.fechaProgramada)}
            <span className="rec-badge-accion">Aplicar</span>
          </span>
        </Link>
      </li>
    );
  }

  return (
    <li className={`recordatorio ${claseEstado}`}>
      <span className="rec-titulo">{recordatorio.titulo}</span>
      <span className="rec-fecha">{formatearFecha(recordatorio.fechaProgramada)}</span>
    </li>
  );
}
